import * as React from "react";
import { makeStyles } from "@mui/styles";
import { colors } from "@/styles/constants";
import { Button } from "@/components/atoms/Button";
import { Sponsors } from "@/components/organisms/Sponsors";
import { SimpleTemplate } from "@/templates/SimpleTemplate";
import { useNavigate } from "react-router-dom";
import { Stack } from "@mui/material";
import { fetchData, saveData } from "@/services/api/openBankingApi";
import { useBrandingContext } from "@/contexts/BrandingContext";

type AccountInfo = {
  displayName: string;
  transactions: Transaction[];
  directDebits: DirectDebit[];
  standingOrders: StandingOrder[];
};

type Transaction = {
  description: string;
  type: string;
  category: string;
  amount: number;
};

type DirectDebit = {
  status: string;
  name: string;
};

type StandingOrder = {
  status: string;
  payee: string;
  reference: string;
};

export type OpenBankingCallbackPageProps = object;

export const OpenBankingCallbackPage: React.FC<OpenBankingCallbackPageProps> = () => {
  const navigate = useNavigate();

  const [accounts, setAccounts] = React.useState(null as null | Array<any>);
  const [error, setError] = React.useState(null as null | string);

  const save = React.useCallback(() => {
    saveData({ accounts }).then(
      () => {
        navigate("/account-search/done", { replace: true });
      },
      (error) => {
        console.warn(error);
        setError("Not saved");
      }
    );
  }, [accounts, navigate]);

  React.useEffect(() => {
    fetchData().then(({ data }) => {
      setAccounts(data);
    }, console.warn);
  }, []);

  const removeErrorMessage = React.useCallback(() => {
    setError(null);
  }, [setError]);

  return (
    <OpenBankingCallbackPageView
      accounts={accounts}
      error={error}
      removeErrorMessage={removeErrorMessage}
      save={save}
    />
  );
};

export type OpenBankingCallbackPageViewProps = {
  accounts: AccountInfo[] | null;
  error: string | null;
  removeErrorMessage: () => void;
  save: () => void;
};

export const OpenBankingCallbackPageView: React.FC<OpenBankingCallbackPageViewProps> = ({
  accounts,
  error,
  removeErrorMessage,
  save,
}) => {
  const classes = useStyles();
  const { brand } = useBrandingContext();

  return (
    <SimpleTemplate
      busy={!accounts}
      headerType="logo"
      errorMessage={
        !error
          ? undefined
          : "Error sharing account information; please try again, or contact customer support."
      }
      removeErrorMessage={removeErrorMessage}
    >
      <div className={classes.textContainer}>
        <Stack spacing={4}>
          <div>
            <p>
              Thank you. The transactions, direct debits and standing orders associated with this
              bank account have now been received.
            </p>

            <p>
              Please click the button below to share this information with {brand}. We will use the
              data you see here, to notify all recognised organisations.
            </p>

            {!accounts ? null : accounts.length === 0 ? (
              <p>No information available</p>
            ) : (
              <Button variant="primary" size="large" onClick={save}>
                {" "}
                Share Now{" "}
              </Button>
            )}
          </div>
          <Sponsors />{" "}
        </Stack>
      </div>
    </SimpleTemplate>
  );
};

const useStyles = makeStyles({
  h1: {
    color: colors.black,
    fontSize: "2rem",
    marginBottom: "1rem",
    fontWeight: 600,
    lineHeight: 1.2,
    marginTop: 0,
  },

  textContainer: {
    color: colors.gray,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flexGrow: 1,
  },

  formContainer: {
    maxWidth: "290px",
  },

  h3: {
    color: colors.veryDarkBlue,
    fontSize: "1.2rem",
    marginBottom: ".5rem",
    fontWeight: "bold",
    lineHeight: 1.2,
    marginTop: 0,
  },

  rightContainer: {
    backgroundColor: colors.lightBlue,
    borderRadius: "5px",
    lineHeight: 2,
    padding: "20px",
  },

  additional: {
    marginBottom: "20px",
  },

  ul: {
    listStyle: "none",
    paddingLeft: 0,
    margin: 0,
  },

  li: {
    "&::before": {
      backgroundColor: "#73a5c4",
      content: '""',
      display: "inline-block",
      height: "10px",
      width: "10px",
      borderRadius: "10px",
      marginRight: "10px",
    },
  },

  sponsors: {
    paddingTop: "30px",
  },

  sponsorsLine: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
  },

  sponsor: {
    padding: "10px",
    textAlign: "center",
  },

  imgWidth: {
    width: "216px",
  },

  imgHeight: {
    height: "65px",
  },
});
